import { get, ref } from "firebase/database";
import React from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import {
  HIST_CSS,
  Legend,
  Marker,
  MarkerTip,
  STAGE,
  Tip,
  hhmm,
} from "../components/historyChart";
import { db } from "../firebase";
import {
  Reboot,
  Row,
  counterRises,
  dayList,
  dayStart,
  downsample,
  findGaps,
  findReboots,
  parseDay,
  stageSegments,
  ticksFor,
  wibClock,
} from "../lib/history";
import { BMS_PROBE_MV, pumpsOn, resetName } from "../lib/teras";

/**
 * Read-only view of /history/teras/<day>, written by the teras board itself.
 * One `get()` per day, never a listener: /history is not a source and a
 * listener on its parent would download all seven days.
 */

const MAX_POINTS = 1500;
const MIN_ZOOM_S = 30;
const DUTY_MAX = 1023;
const LAMP_MAX = 255;
const PUMP_Y = [4, 3, 2, 1]; // lane height of P1..P4

/** Lane order, top to bottom. */
const LANES = [
  { key: "reboot", label: "reboot", shape: "triangle", color: "var(--c5)" },
  { key: "b", label: "BMS cut", shape: "diamond", color: "var(--c2)" },
  { key: "px", label: "probe > 14 V", shape: "square", color: "var(--c4)" },
  { key: "r", label: "VBATT railed", shape: "cross", color: "var(--c1)" },
  { key: "e", label: "publish gagal", shape: "circle", color: "var(--c3)" },
] as const;

interface Point {
  t: number;
  v?: number;
  p?: number;
  s?: number;
  vr?: [number, number];
  ma?: number;
  pos?: number;
  neg?: number;
  mr?: [number, number];
  o?: number;
  d?: number;
  c?: number;
  l?: number;
  p1?: number;
  p2?: number;
  p3?: number;
  p4?: number;
  [temp: string]: number | [number, number] | undefined;
}

const scale = (x: number | undefined, by: number) => (x === undefined ? undefined : x / by);
const pct = (x: number | undefined, max: number) =>
  x === undefined ? undefined : (x * 100) / max;
const range = (lo?: number, hi?: number, by = 1): [number, number] | undefined =>
  lo === undefined || hi === undefined ? undefined : [lo / by, hi / by];

function toPoint(r: Row): Point {
  const p: Point = {
    t: r.t,
    v: scale(r.v, 1000),
    p: scale(r.p, 1000),
    s: scale(r.s, 1000),
    vr: range(r.vn, r.vx, 1000),
    ma: scale(r.ma, 1000),
    mr: range(r.mn, r.mx, 1000),
    o: pct(r.o, DUTY_MAX),
    d: pct(r.d, DUTY_MAX),
    c: pct(r.c, DUTY_MAX),
    l: pct(r.l, LAMP_MAX),
    ...r.temps,
  };
  if (p.ma !== undefined) {
    p.pos = Math.max(p.ma, 0);
    p.neg = Math.min(p.ma, 0);
  }
  if (r.pm !== undefined) {
    const on = pumpsOn(r.pm);
    PUMP_Y.forEach((y, i) => {
      if (on.includes(i + 1)) p[`p${i + 1}`] = y;
    });
  }
  return p;
}

const dayLabel = (d: string, today: string) =>
  `${d.slice(6)}/${d.slice(4, 6)}/${d.slice(0, 4)}${d === today ? " (hari ini)" : ""}`;

const volts = (mv?: number) => (mv === undefined ? "--" : (mv / 1000).toFixed(2).replace(".", ",") + " V");
const amps = (ma?: number) =>
  ma === undefined ? "--" : (ma / 1000).toFixed(2).replace(".", ",").replace("-", "−") + " A";
const uptime = (s?: number) =>
  s === undefined ? "--" : `${Math.floor(s / 3600)} j ${Math.floor((s % 3600) / 60)} m`;

function Card({ title, value, sub }: { title: string; value: string; sub?: string }) {
  return (
    <div className="rounded border border-slate-500/50 p-2">
      <div className="text-xs opacity-60">{title}</div>
      <div className="text-lg font-bold">{value}</div>
      {sub && <div className="text-xs opacity-60">{sub}</div>}
    </div>
  );
}

export default function Teras() {
  const days = React.useMemo(() => dayList(Date.now()), []);
  const [day, setDay] = React.useState(days[0]);
  const [rows, setRows] = React.useState<Row[]>([]);
  const [status, setStatus] = React.useState<"loading" | "ok" | "error">("loading");
  const [error, setError] = React.useState("");
  const [zoom, setZoom] = React.useState<[number, number] | null>(null);
  const [sel, setSel] = React.useState<{ a: number; b?: number } | null>(null);
  const [showTemp, setShowTemp] = React.useState(false);
  const req = React.useRef(0);

  const load = React.useCallback((d: string) => {
    const me = ++req.current; // a slow earlier day must not overwrite a newer pick
    setStatus("loading");
    get(ref(db, "history/teras/" + d))
      .then((ss) => {
        if (me !== req.current) return;
        setRows(parseDay(ss.val()));
        setStatus("ok");
      })
      .catch((err) => {
        if (me !== req.current) return;
        setRows([]);
        setError(String(err?.message ?? err));
        setStatus("error");
      });
  }, []);

  React.useEffect(() => {
    setZoom(null);
    setSel(null);
    load(day);
  }, [day, load]);

  const a = React.useMemo(() => {
    const gaps = findGaps(rows);
    const reboots = findReboots(rows);
    const rises = counterRises(rows).filter((r) => r.key !== "om");
    const idxOfT = new Map(rows.map((r, i) => [r.t, i]));
    const probeIdx = rows.flatMap((r, i) => ((r.px ?? 0) >= BMS_PROBE_MV ? [i] : []));
    return {
      gaps,
      reboots,
      segs: stageSegments(rows, gaps),
      rises,
      probeIdx,
      forceIdx: [...rises.map((r) => idxOfT.get(r.t) as number), ...probeIdx],
      tempKeys: Array.from(new Set(rows.flatMap((r) => Object.keys(r.temps)))).sort(),
    };
  }, [rows]);

  const full: [number, number] = React.useMemo(() => {
    const t0 = dayStart(day);
    return [t0, t0 + 86400];
  }, [day]);
  const [d0, d1] = zoom ?? full;

  // Downsample only what is on screen, so zooming in recovers the detail.
  const points = React.useMemo(() => {
    const lo = rows.findIndex((r) => r.t >= d0);
    if (lo < 0) return [];
    let hi = rows.length - 1;
    while (hi > lo && rows[hi].t > d1) hi--;
    const view = rows.slice(lo, hi + 1);
    const gapSet = new Set(a.gaps.map((g) => g - lo));
    const force = new Set<number>();
    gapSet.forEach((g) => {
      force.add(g);
      force.add(g - 1);
    });
    a.reboots.forEach((r) => force.add(r.index - lo));
    a.forceIdx.forEach((i) => force.add(i - lo));
    const out: Point[] = [];
    let prevT = 0;
    for (const i of downsample(view.length, MAX_POINTS, force)) {
      // A bare point makes the line (and area) break instead of bridging a gap.
      if (gapSet.has(i) && prevT && view[i].t - prevT > 1) out.push({ t: prevT + 1 });
      out.push(toPoint(view[i]));
      prevT = view[i].t;
    }
    return out;
  }, [rows, a, d0, d1]);

  const markers = React.useMemo(() => {
    const out: Record<string, Marker[]> = {};
    const add = (key: string, m: Marker) => (out[key] = [...(out[key] ?? []), m]);
    const yOf = (key: string) => LANES.length - 1 - LANES.findIndex((l) => l.key === key);
    a.reboots.forEach((rb: Reboot) =>
      add("reboot", {
        t: rb.t,
        y: yOf("reboot"),
        title: "reboot",
        lines: [`alasan reset: ${resetName(rb.rr)}`],
      })
    );
    a.rises.forEach((r) =>
      add(r.key, {
        t: r.t,
        y: yOf(r.key),
        title: LANES.find((l) => l.key === r.key)?.label ?? r.key,
        lines: [`+${r.delta}`],
      })
    );
    a.probeIdx.forEach((i) =>
      add("px", {
        t: rows[i].t,
        y: yOf("px"),
        title: "probe ON tinggi",
        lines: [`${volts(rows[i].px)} (≥ ${BMS_PROBE_MV / 1000} V = BMS memutus)`],
      })
    );
    return out;
  }, [a, rows]);

  const inView = (t: number) => t >= d0 && t <= d1;
  const xAxis = (last: boolean) => (
    <XAxis
      type="number"
      dataKey="t"
      domain={[d0, d1]}
      allowDataOverflow
      ticks={ticksFor(d0, d1)}
      tickFormatter={hhmm}
      tick={last ? undefined : false}
      height={last ? 22 : 4}
      stroke="var(--grid)"
    />
  );
  const grid = <CartesianGrid stroke="var(--grid)" vertical={false} />;
  // `yAxisId` is needed in charts whose Y axes are named: reference elements
  // otherwise look up axis "0" and throw.
  const rebootLines = (yAxisId?: string) =>
    a.reboots
      .filter((r) => inView(r.t))
      .map((r) => (
        <ReferenceLine
          key={r.t}
          x={r.t}
          yAxisId={yAxisId}
          stroke="var(--c5)"
          strokeDasharray="3 3"
          strokeOpacity={0.7}
        />
      ));

  const drag = {
    syncId: "teras",
    syncMethod: "value" as const,
    onMouseDown: (e: any) => e?.activeLabel != null && setSel({ a: Number(e.activeLabel) }),
    onMouseMove: (e: any) =>
      sel && e?.activeLabel != null && setSel({ a: sel.a, b: Number(e.activeLabel) }),
    onMouseUp: () => {
      if (sel?.b != null && Math.abs(sel.b - sel.a) >= MIN_ZOOM_S)
        setZoom([Math.min(sel.a, sel.b), Math.max(sel.a, sel.b)]);
      setSel(null);
    },
  };
  const selArea = (yAxisId?: string) =>
    sel?.b != null ? (
      <ReferenceArea x1={sel.a} x2={sel.b} yAxisId={yAxisId} fill="currentColor" fillOpacity={0.12} />
    ) : null;
  const margin = { top: 6, right: 8, bottom: 0, left: 0 };
  const axisTick = { fontSize: 11 };
  const lastRow = rows.length ? rows[rows.length - 1] : undefined;
  const pumps = pumpsOn(lastRow?.pm);

  const emptyMsg =
    status === "loading"
      ? "Memuat…"
      : status === "ok" && rows.length === 0
      ? "Tidak ada rekaman untuk hari ini."
      : null;

  return (
    <div className="hist px-2 pb-10">
      <style>{HIST_CSS}</style>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h1 className="text-lg font-bold mr-2">Teras</h1>
        <select
          value={day}
          onChange={(e) => setDay(e.target.value)}
          className="rounded border border-slate-500/50 bg-transparent px-2 py-1 text-sm"
        >
          {days.map((d) => (
            <option key={d} value={d} className="text-black">
              {dayLabel(d, days[0])}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => load(day)}
          className="rounded border border-slate-500/50 px-2 py-1 text-sm hover:bg-slate-500/20"
        >
          <i className="fas fa-rotate mr-1" />
          Refresh
        </button>
        {zoom && (
          <button
            type="button"
            onClick={() => setZoom(null)}
            className="rounded border border-slate-500/50 px-2 py-1 text-sm hover:bg-slate-500/20"
          >
            Reset zoom ({hhmm(zoom[0])}–{hhmm(zoom[1])})
          </button>
        )}
        <span className="text-xs opacity-60">
          {status === "ok" && lastRow
            ? `Terakhir diperbarui ${wibClock(lastRow.t)} WIB · ${rows.length} baris · seret pada chart untuk zoom`
            : ""}
        </span>
      </div>

      {status === "error" && (
        <div className="rounded border border-red-500/50 bg-red-500/10 p-3 text-sm">
          Gagal membaca <code>/history/teras/{day}</code>: {error}
          {/permission/i.test(error) && (
            <div className="mt-1 opacity-80">
              Rule RTDB untuk <code>/history</code> belum mengizinkan baca. Rule diatur di
              Firebase console, bukan di repo ini.
            </div>
          )}
        </div>
      )}
      {emptyMsg && <div className="py-10 text-center opacity-70">{emptyMsg}</div>}

      {status === "ok" && lastRow && (
        <div className="select-none">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <Card title="Board" value={volts(lastRow.v)} />
            <Card title="Pack" value={volts(lastRow.p)} />
            <Card title="Panel" value={volts(lastRow.s)} />
            <Card
              title="Arus baterai"
              value={amps(lastRow.ma)}
              sub={lastRow.ma === undefined ? undefined : lastRow.ma > 0 ? "mengisi" : "beban"}
            />
            <Card
              title="Charger"
              value={lastRow.g === undefined ? "--" : STAGE[lastRow.g]?.name ?? String(lastRow.g)}
            />
            <Card
              title="Lampu"
              value={lastRow.l === undefined ? "--" : `${Math.round(pct(lastRow.l, LAMP_MAX) as number)}%`}
            />
            <Card title="Pompa" value={pumps.length ? pumps.map((n) => "P" + n).join(" ") : "mati"} />
            <Card title="Uptime" value={uptime(lastRow.up)} />
          </div>

          <Legend
            items={[
              ["var(--c1)", "Board (v)"],
              ["var(--c3)", "Pack (p)"],
              ["var(--c2)", "Panel (s)"],
              ...[0, 1, 2, 3].map((g) => [STAGE[g].color, STAGE[g].name] as [string, string]),
            ]}
          />
          <div style={{ height: 200 }}>
            <ResponsiveContainer>
              <ComposedChart data={points} margin={margin} {...drag}>
                {grid}
                {xAxis(false)}
                <YAxis width={44} tick={axisTick} stroke="var(--grid)" unit=" V" domain={["auto", "auto"]} />
                {a.segs.map((s) => (
                  <ReferenceArea
                    key={s.start}
                    x1={s.start}
                    x2={s.end}
                    fill={STAGE[s.g]?.color ?? "#888"}
                    fillOpacity={0.16}
                    ifOverflow="hidden"
                  />
                ))}
                {rebootLines()}
                <Area dataKey="vr" stroke="none" fill="var(--c1)" fillOpacity={0.18} isAnimationActive={false} />
                <Line dataKey="v" stroke="var(--c1)" dot={false} strokeWidth={1.5} isAnimationActive={false} />
                <Line dataKey="p" stroke="var(--c3)" dot={false} strokeWidth={2} isAnimationActive={false} />
                <Line dataKey="s" stroke="var(--c2)" dot={false} strokeWidth={1.5} isAnimationActive={false} />
                {selArea()}
                <Tooltip
                  isAnimationActive={false}
                  content={<Tip fmt={{ v: ["Board", "V", 2], p: ["Pack", "V", 2], s: ["Panel", "V", 2] }} />}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="text-xs opacity-60 px-1 mb-3">
            Area biru muda = rentang vn–vx board antar baris. Saat beban besar sisi board turun di
            bawah pack (selisih ≈ arus × 0,1075 Ω).
          </div>

          <Legend items={[["var(--c3)", "Mengisi (+)"], ["var(--c2)", "Dipakai (−)"]]} />
          <div style={{ height: 150 }}>
            <ResponsiveContainer>
              <ComposedChart data={points} margin={margin} {...drag}>
                {grid}
                {xAxis(false)}
                <YAxis width={44} tick={axisTick} stroke="var(--grid)" unit=" A" />
                <Area dataKey="pos" stroke="none" fill="var(--c3)" fillOpacity={0.45} isAnimationActive={false} />
                <Area dataKey="neg" stroke="none" fill="var(--c2)" fillOpacity={0.45} isAnimationActive={false} />
                <Area dataKey="mr" stroke="none" fill="currentColor" fillOpacity={0.12} isAnimationActive={false} />
                <Line dataKey="ma" stroke="currentColor" strokeOpacity={0.7} dot={false} strokeWidth={1} isAnimationActive={false} />
                <ReferenceLine y={0} stroke="currentColor" strokeWidth={1.5} />
                {rebootLines()}
                {selArea()}
                <Tooltip isAnimationActive={false} content={<Tip fmt={{ ma: ["Arus", "A", 3] }} />} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="text-xs opacity-60 px-1 mb-3">
            Diukur saat charger di-blank, jadi di siang hari ini arus beban, bukan arus bersih aki.
            Area abu-abu = rentang mn–mx antar baris.
          </div>

          <Legend
            items={[
              ["var(--c1)", "o: ke pin"],
              ["var(--c2)", "d: integrator"],
              ["var(--c3)", "c: plafon"],
            ]}
          />
          <div style={{ height: 130 }}>
            <ResponsiveContainer>
              <ComposedChart data={points} margin={margin} {...drag}>
                {grid}
                {xAxis(false)}
                <YAxis width={44} tick={axisTick} stroke="var(--grid)" unit="%" domain={[0, 100]} />
                <Line dataKey="c" stroke="var(--c3)" dot={false} strokeWidth={1.5} isAnimationActive={false} />
                <Line dataKey="d" stroke="var(--c2)" dot={false} strokeWidth={1.5} isAnimationActive={false} />
                <Line dataKey="o" stroke="var(--c1)" dot={false} strokeWidth={2} isAnimationActive={false} />
                {rebootLines()}
                {selArea()}
                <Tooltip
                  isAnimationActive={false}
                  content={<Tip fmt={{ o: ["out", "%", 1], d: ["duty", "%", 1], c: ["plafon", "%", 1] }} />}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <Legend items={[["var(--c4)", "Lampu (l)"], ["var(--c1)", "Pompa P1–P4 (pm)"]]} />
          <div style={{ height: 150 }}>
            <ResponsiveContainer>
              <ComposedChart data={points} margin={margin} {...drag}>
                {grid}
                {xAxis(false)}
                <YAxis yAxisId="l" width={44} tick={axisTick} stroke="var(--grid)" unit="%" domain={[0, 100]} />
                <YAxis yAxisId="pump" hide domain={[0.4, 4.6]} />
                <Area yAxisId="l" dataKey="l" stroke="var(--c4)" fill="var(--c4)" fillOpacity={0.2} strokeWidth={1.5} dot={false} isAnimationActive={false} />
                {PUMP_Y.map((_, i) => (
                  <Line
                    key={i}
                    yAxisId="pump"
                    dataKey={`p${i + 1}`}
                    stroke="var(--c1)"
                    strokeWidth={5}
                    strokeOpacity={0.8}
                    dot={false}
                    isAnimationActive={false}
                  />
                ))}
                {rebootLines("l")}
                {selArea("l")}
                <Tooltip
                  isAnimationActive={false}
                  content={<Tip fmt={{ l: ["lampu", "%", 0] }} />}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="text-xs opacity-60 px-1 mb-3">
            Garis tebal dari atas ke bawah = P1, P2, P3, P4 (bit 1, 2, 4, 8 dari pm).
          </div>

          <Legend items={LANES.map((l) => [l.color, l.label] as [string, string])} />
          <div style={{ height: 130 }}>
            <ResponsiveContainer>
              <ComposedChart margin={margin} {...drag}>
                {grid}
                {xAxis(true)}
                <YAxis
                  type="number"
                  dataKey="y"
                  width={44}
                  domain={[-0.6, 4.6]}
                  ticks={[0, 1, 2, 3, 4]}
                  tickFormatter={(y) => LANES[4 - y]?.label.split(" ")[0] ?? ""}
                  tick={{ fontSize: 10 }}
                  stroke="var(--grid)"
                  allowDataOverflow
                />
                <ZAxis range={[70, 70]} />
                {LANES.map((l) => (
                  <Scatter
                    key={l.key}
                    data={(markers[l.key] ?? []).filter((m) => inView(m.t))}
                    dataKey="y"
                    shape={l.shape}
                    fill={l.color}
                    isAnimationActive={false}
                  />
                ))}
                {rebootLines()}
                {selArea()}
                <Tooltip isAnimationActive={false} content={<MarkerTip />} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="text-xs opacity-60 px-1 mb-3">
            Penanda b/r/e muncul saat penghitung naik (dihitung per boot); garis merah putus-putus =
            reboot. Celah &gt; 2,5× jarak baris memutus garis.
          </div>

          {a.tempKeys.length > 0 && (
            <>
              <button
                type="button"
                className="text-sm underline opacity-80 mb-2"
                onClick={() => setShowTemp((s) => !s)}
              >
                {showTemp ? "Sembunyikan" : "Tampilkan"} suhu
              </button>
              {showTemp && (
                <div style={{ height: 120 }}>
                  <ResponsiveContainer>
                    <ComposedChart data={points} margin={margin} {...drag}>
                      {grid}
                      {xAxis(false)}
                      <YAxis width={44} tick={axisTick} stroke="var(--grid)" unit="°" domain={["auto", "auto"]} />
                      {a.tempKeys.map((k, i) => (
                        <Line key={k} dataKey={k} stroke={`var(--c${i + 1})`} dot={false} strokeWidth={1.5} isAnimationActive={false} />
                      ))}
                      {rebootLines()}
                      {selArea()}
                      <Tooltip
                        isAnimationActive={false}
                        content={<Tip fmt={Object.fromEntries(a.tempKeys.map((k) => [k, [k, "°C", 1]]))} />}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
